<script lang="ts" setup>
    import IconButton from '../IconButton.vue';
    import InputLabel from '../InputLabel.vue';
    import EditCoachingFormQuestion from './EditCoachingFormQuestion.vue';
    import { ICoachingFormSection } from '@/js/services/api/coachingForm/types';
    import { computed, nextTick, ref } from 'vue';
    import { useEditCoachingFormStore } from '@/js/stores/edit-coaching-form';
    import CheckboxInput from '../CheckboxInput.vue';
    import { storeToRefs } from 'pinia';
    import { __ } from '@/js/stores/expressions';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import EchoButton from '../EchoButton.vue';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import { useSectionColor } from '@/js/composables/useSectionColor';
    import { ChevronDownIcon, ChevronUpIcon, PlusIcon, XIcon } from '@lucide/vue';
    import ErrorMessage from '../ErrorMessage.vue';

    const collapsed = ref(false);

    const { addQuestion, removeSection, sortMoveUpSection, sortMoveDownSection, isLastSection } =
        useEditCoachingFormStore();
    const { sortForm } = storeToRefs(useEditCoachingFormStore());

    const { setFocus, rememberFocus, restoreFocus } = useElementFocus();
    const { getCardAccentColor } = useSectionColor();

    const section = defineModel<ICoachingFormSection>('section', { required: true });
    const props = defineProps<{
        sectionIndex: number;
        useAi: boolean;
    }>();

    const sectionPoints = computed(() => {
        return section.value.questions.reduce((total, question) => {
            const questionTotal =
                question.answers.length === 0
                    ? 0
                    : Math.max(...question.answers.map((answer) => answer.points));
            return questionTotal + total;
        }, 0);
    });

    const addQuestionAndSetFocus = () => {
        const question = addQuestion(props.sectionIndex);
        setFocus(`#question-title-${question.id}`);
    };

    const sortingQuestions = ref(false);

    const onSortingQuestions = () => {
        sortingQuestions.value = true;

        nextTick(() => {
            sortingQuestions.value = false;
        });
    };
</script>

<template>
    <div
        class="rounded-lg border border-dashed border-gray-light bg-gray-extralight overflow-hidden mb-4"
    >
        <div class="flex items-center gap-3 px-5 py-4 border-b border-dashed border-gray-light">
            <span
                class="w-2 h-7.5 rounded-full shrink-0"
                :class="getCardAccentColor(sectionIndex)"
            />
            <button
                type="button"
                class="text-gray-medium shrink-0 cursor-pointer flex"
                :aria-expanded="!collapsed"
                :aria-label="
                    collapsed
                        ? __('expand-section', { section: section.title })
                        : __('collapse-section', { section: section.title })
                "
                @click="collapsed = !collapsed"
            >
                <ChevronDownIcon
                    class="size-4.5 transition-transform"
                    :class="{ '-rotate-90': collapsed }"
                />
            </button>
            <h3 class="heading-md flex-1 min-w-0 truncate" :title="section.title">
                {{ sectionIndex + 1 }}. {{ section.title }}
            </h3>
            <div class="flex items-center gap-3 shrink-0">
                <template v-if="!sortForm">
                    <p class="text-body text-gray-medium">
                        {{ __('coaching-form-section-points') }}
                    </p>
                    <!-- Earned points are always 0 here: the builder has no answered protocol to score, this badge only previews the max-points layout used on the live coaching-fill screen -->
                    <div
                        class="flex items-center border border-gray-light rounded-full bg-white overflow-hidden"
                    >
                        <span class="text-body-bold px-3.5 py-1.5 whitespace-nowrap">
                            0 {{ __('coaching-form-points-of') }} {{ sectionPoints }}
                        </span>
                        <span class="text-body-bold px-3.5 py-1.5 border-l border-gray-light">
                            0%
                        </span>
                    </div>
                    <IconButton
                        class="text-gray-medium hover:bg-gray-lightest hover:text-error rounded-sm"
                        area-size="size-8"
                        size="size-4.5"
                        :aria-label="__('remove-section', { section: section.title })"
                        @click="removeSection(sectionIndex)"
                    >
                        <XIcon class="size-4.5" />
                    </IconButton>
                </template>

                <template v-else>
                    <div class="flex flex-col gap-1">
                        <IconButton
                            class="text-gray-dark bg-white"
                            area-size="w-8 h-6"
                            size="size-6"
                            type="button"
                            :aria-label="__('sort-section-up', { section: section.title })"
                            :disabled="sectionIndex === 0"
                            @click="
                                rememberFocus();
                                sortMoveUpSection(sectionIndex);
                                restoreFocus();
                            "
                        >
                            <ChevronUpIcon class="size-5" />
                        </IconButton>
                        <IconButton
                            class="text-gray-dark bg-white"
                            area-size="w-8 h-6"
                            size="size-6"
                            type="button"
                            :aria-label="__('sort-section-down', { section: section.title })"
                            :disabled="isLastSection(sectionIndex)"
                            @click="
                                rememberFocus();
                                sortMoveDownSection(sectionIndex);
                                restoreFocus();
                            "
                        >
                            <ChevronDownIcon class="size-5" />
                        </IconButton>
                    </div>
                </template>
            </div>
        </div>

        <div v-if="!collapsed">
            <div v-if="!sortForm" class="px-6 py-5">
                <InputLabel :for="`section-title-${section.id}`">
                    {{ __('coaching-form-section-title') }}
                </InputLabel>
                <TextInput
                    :id="`section-title-${section.id}`"
                    v-model="section.title"
                    :width="TextInputWidth.Large"
                />

                <div class="mt-5">
                    <CheckboxInput
                        :id="`section-use-comments-${section.id}`"
                        v-model="section.useComments"
                    >
                        {{ __('coaching-form-section-use-comments') }}
                    </CheckboxInput>
                </div>
            </div>
            <TransitionGroup name="animate-question" tag="div" class="relative">
                <EditCoachingFormQuestion
                    v-for="(question, questionIndex) in section.questions"
                    :key="question.id"
                    :section-index="sectionIndex"
                    :question-index="questionIndex"
                    :question="question"
                    :use-ai="useAi"
                    :animating="sortingQuestions"
                    @sorting="onSortingQuestions"
                />
            </TransitionGroup>
            <div class="px-6">
                <ErrorMessage :validation-key="'sections.' + sectionIndex + '.questions'" />
            </div>
            <div v-if="!sortForm" class="px-6 pt-3 pb-5 border-t border-dashed border-gray-light">
                <EchoButton variant="dashed" size="small" @click="addQuestionAndSetFocus()">
                    <template #icon-left><PlusIcon /></template>
                    {{ __('add-question') }}
                </EchoButton>
            </div>
        </div>
    </div>
</template>

<style scoped>
    :deep(.animate-question-move[data-currently-animating='true']) {
        transition:
            opacity 0.3s ease-in-out,
            transform 0.3s ease-in-out;
        will-change: transform;
    }

    :deep(.animate-question-enter-active),
    :deep(.animate-question-leave-active) {
        transition: opacity 0.3s ease-in-out;
    }

    :deep(.animate-question-enter-from),
    :deep(.animate-question-leave-to) {
        opacity: 0;
    }
</style>
